import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { checkCourseAccess } from "@/lib/access.functions";

export type ResumeState = "hidden" | "loading" | "ready";

/**
 * Three-state resume gate:
 *   - "hidden"  → no session, or access definitively denied. Don't render.
 *   - "loading" → session exists, access check in flight. Render optimistically
 *                 so freshly-paid users (whose `purchases` row may not yet be
 *                 visible due to Stripe-webhook race) still see the CTA.
 *   - "ready"   → session + confirmed paid access.
 *
 * Defaults to "hidden" on SSR / first paint so anonymous visitors never get a
 * false-positive flash.
 */
export function useResumeState(): ResumeState {
  const [state, setState] = useState<ResumeState>("hidden");

  useEffect(() => {
    let cancelled = false;

    const evaluate = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) {
          if (!cancelled) setState("hidden");
          return;
        }
        // Signed in — show optimistically while the access check resolves.
        if (!cancelled) setState("loading");
        const result = await checkCourseAccess();
        if (cancelled) return;
        setState(result?.hasAccess ? "ready" : "hidden");
      } catch {
        // Network / server error: stay optimistic if we already showed it.
        if (!cancelled) {
          setState((prev) => (prev === "loading" ? "loading" : "hidden"));
        }
      }
    };

    evaluate();

    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      evaluate();
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  return state;
}

/**
 * Backward-compatible boolean: true when we should render the Resume CTA.
 * Includes the optimistic "loading" state to avoid hiding the button during
 * the post-checkout webhook race.
 */
export function useCanResume(): boolean {
  const state = useResumeState();
  return state !== "hidden";
}
